import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import useSkin from "src/hooks/useSkin";
import { RootReducer } from "src/redux/reducers";
import {
  Wrapper,
  TriggerButton,
  ClassName,
  ChevronIcon,
  DropdownPanel,
  DropdownTitle,
  ClassItem,
  ClassDot,
  ClassLabel,
  StudentCount
} from "./ClassSwitcher.styles";

interface ClassInfo {
  id: number;
  name: string;
  studentCount: number;
}

const ChevronSvg = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ClassSwitcher: React.FC = () => {
  const [t] = useTranslation();
  const { isGriffle } = useSkin();
  const [isOpen, setIsOpen] = useState(false);
  const [activeClassId, setActiveClassId] = useState<number | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { group } = useSelector<RootReducer, RootReducer>((state) => state);

  const classes: ClassInfo[] = group.groups?.rows
    ? group.groups.rows.map((row) => ({
        id: row.id,
        name: row.name,
        studentCount: parseInt(row.usersCount, 10) || 0
      }))
    : [];

  useEffect(() => {
    if (activeClassId === null && classes.length > 0) {
      setActiveClassId(classes[0].id);
    }
  }, [classes, activeClassId]);

  const activeClass = classes.find((c) => c.id === activeClassId);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleSelect = useCallback((id: number) => {
    setActiveClassId(id);
    setIsOpen(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (classes.length === 0) return null;

  return (
    <Wrapper ref={wrapperRef}>
      <TriggerButton $isGriffle={isGriffle} $isOpen={isOpen} onClick={handleToggle}>
        <ClassName $isGriffle={isGriffle}>{activeClass?.name || t("select_class", "Select class")}</ClassName>
        <ChevronIcon $isOpen={isOpen}>
          <ChevronSvg />
        </ChevronIcon>
      </TriggerButton>

      {isOpen && (
        <DropdownPanel $isGriffle={isGriffle}>
          <DropdownTitle>{t("your_classes", "Your classes")}</DropdownTitle>
          {classes.map((cls) => (
            <ClassItem
              key={cls.id}
              $isGriffle={isGriffle}
              $isActive={cls.id === activeClassId}
              onClick={() => handleSelect(cls.id)}
            >
              <ClassDot $isGriffle={isGriffle} $isActive={cls.id === activeClassId} />
              <ClassLabel $isGriffle={isGriffle} $isActive={cls.id === activeClassId}>
                {cls.name}
              </ClassLabel>
              <StudentCount>
                {cls.studentCount} {t("students", "students")}
              </StudentCount>
            </ClassItem>
          ))}
        </DropdownPanel>
      )}
    </Wrapper>
  );
};

export default ClassSwitcher;
